/* eslint-disable camelcase -- PostHog's own config keys (api_host, before_send, …) */
import type { CapturedNetworkRequest, PostHogConfig } from "posthog-js";

import { cleanUrl, sanitizeEvent } from "./posthog-sanitize";

/** `envConfig.posthog`: absent where the environment has no project. */
export type PosthogSettings = { key: string; host: string };

const REPLAY_KEPT_ATTRIBUTES = new Set([
  "class",
  "style",
  "role",
  "dir",
  "tabindex",
  "type",
  // rrweb's inlined stylesheet text: CSS rules only, no user data
  "_cssText",
  // Structural only: index.html sizes the app through `#app-root`, and the
  // app itself sets no ids (no nativeID), so no user data lives here
  "id",
  // SVG presentation and geometry
  "d",
  "viewBox",
  "x",
  "y",
  "x1",
  "y1",
  "x2",
  "y2",
  "cx",
  "cy",
  "r",
  "rx",
  "ry",
  "width",
  "height",
  "points",
  "transform",
  "fill",
  "fill-rule",
  "clip-rule",
  "stroke",
  "stroke-width",
  "stroke-linecap",
  "stroke-linejoin",
  "opacity",
  "xmlns",
  "preserveAspectRatio",
]);

// react-native-web draws <Image> as an inline `background-image: url(…)`,
// set once the image has loaded, so the kept `style` attribute would carry
// wizard photo URLs into the recording.
const CSS_URL = /url\(\s*(?:"[^"]*"|'[^']*'|[^)]*)\s*\)/gi;

/**
 * Replay attribute allow-list. rrweb serialises attributes as-is, and v1
 * renders answer values into data-testid and labels into aria-label, so
 * everything not needed to draw the page is masked.
 */
export function maskReplayAttribute(name: string, value: string): string {
  if (name === "style") return value.replace(CSS_URL, "url()");
  if (REPLAY_KEPT_ATTRIBUTES.has(name) || name.startsWith("rr_")) return value;
  return "***";
}

/** Replay network capture: keep timing, drop query strings, headers and bodies. */
export function maskReplayNetworkRequest(
  request: CapturedNetworkRequest,
): CapturedNetworkRequest {
  return {
    ...request,
    name: cleanUrl(request.name),
    requestHeaders: undefined,
    responseHeaders: undefined,
    requestBody: undefined,
    responseBody: undefined,
  };
}

/**
 * Everything PostHog may collect in family web v1, in one place. Onboarding
 * answers are buttons labelled with health data, so autocapture and replay
 * keep no text at all. `isInScope` reports whether the family is on an
 * onboarding screen right now (posthog.web.ts).
 */
export function buildPosthogConfig(
  host: string,
  isInScope: () => boolean,
): Partial<PostHogConfig> {
  return {
    api_host: host,
    person_profiles: "identified_only",
    // Page views and recording are started per onboarding route by
    // posthogRouteSettled; PostHog's own would fire on every v1 page load.
    capture_pageview: false,
    disable_session_recording: true,
    capture_pageleave: true,
    autocapture: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    // Network timing would record request URLs such as /token?token=…
    capture_performance: false,
    enable_recording_console_log: false,
    // Flag requests would send the raw landing URL, bypassing before_send.
    advanced_disable_feature_flags: true,
    capture_heatmaps: false,
    // Not the 365-day parent-domain cookie.
    persistence: "localStorage",
    mask_personal_data_properties: true,
    custom_personal_data_properties: [
      "hash",
      "email",
      "phone",
      "identifier",
      "identifierType",
      "id",
      "code",
      "token",
    ],
    capture_exceptions: false,
    // Console log capture can be switched on from the project settings
    // whatever the local config says, and log records never pass before_send
    // or the onboarding gate. Drop them all.
    logs: { beforeSend: () => null },
    disable_surveys: true,
    disable_product_tours: true,
    disable_conversations: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
      blockSelector: "[data-ph-no-capture]",
      recordHeaders: false,
      recordBody: false,
      captureCanvas: { recordCanvas: false },
      maskAttributeFn: maskReplayAttribute,
      maskCapturedNetworkRequestFn: maskReplayNetworkRequest,
    },
    before_send: (event) => {
      if (!event) return null;
      // Snapshot batches flush seconds after they are recorded, so the last
      // one of an onboarding page arrives after the family has left.
      // Recording itself is started and stopped per route.
      if (event.event !== "$snapshot" && !isInScope()) return null;
      return sanitizeEvent(event);
    },
  };
}
